import { Alert, Button, Flex, Modal, SegmentedControl, Stack, Text, Title, Tooltip } from "@mantine/core";
import { ISpecificationTable } from "@mctypes/frontend.types";
import { IApp } from "@mctypes/global.types";
import { IUserPartial } from "@schemas/issueSchema";
import { IconInfoCircle, IconMailFilled, IconSearch } from "@tabler/icons-react";
import React, { useMemo } from "react";
import { STANDARD_COLORS } from "../../app/globalConstants";
import { doSendUserMessage } from "../../app/routes/admin/restQueries";
import { setShowSuccessMessageThunk } from "../../global/globalSlice";
import { useAppDispatch } from "../../hooks";
import { ChecklistDisciplineRoles } from "@mcapps/shared/enums";
import { MCApplicationsUrlEnum } from "@mcapps/shared/sharedConstants";
import ContactForm from "../ContactForm/ContactForm";
import ExcelImportValidatorWithSpecTable from "../ExcelImportValidator/ExcelImportValidatorWithSpecTable";
import HierarchyTreeSelector from "../HierarchyTreeSelector/HierarchyTreeSelector";
import McInputNew from "../McInput/McInputNew";
import SimpleStampCheck from "../SimpleStampCheck/SimpleStampCheck";
import UserAvatar from "../Avatar";
import "./TemplateSelector.css";

export type TemplateSelectorType = "MC" | "PROJECT" | "INSTANCE" | "EXCEL";

export interface ITemplate {
  _id: string;
  name: string;
  templateType: TemplateSelectorType;
  category?: string | null;
  [key: string]: any;
  description?: string;
  admin?: IUserPartial;
}

export interface IHierarchyCategory {
  _id: string;
  parentId: string;
  props: {
    templateId: string | null;
    title: string | null;
  };
}

const TemplateSelector: React.FC<{
  app: Pick<IApp, "icon" | "iconColor" | "name">;
  header: string;
  templates: ITemplate[];
  onCreate: (template: ITemplate) => void;
  closeModal: () => void;
  isTemplateProject: boolean;
  isTitleEmpty?: boolean;
  descriptionAndAdmin?: boolean;
  showMCButton?: boolean;
  showProjectButton?: boolean;
  showInstanceButton?: boolean;
  preSelectedTemplate?: ITemplate;
  disableOpprettButton?: boolean;
  createLoading?: boolean;
  onTemplateSelected?: (template: ITemplate) => void;
  excelImport: {
    enabled: boolean;
    specificationTable?: ISpecificationTable;
    onCreateFromExcel?: (data: string[][]) => void;
    zodValidation?: any;
  };
  bottomDescriptionSection?: React.ReactNode;
}> = ({
  app,
  header,
  children,
  templates,
  onCreate,
  closeModal,
  isTemplateProject,
  isTitleEmpty,
  descriptionAndAdmin,
  showMCButton = true,
  showProjectButton = true,
  showInstanceButton = true,
  preSelectedTemplate,
  disableOpprettButton,
  createLoading = false,
  onTemplateSelected,
  excelImport,
  bottomDescriptionSection,
}) => {
  const [filter, setFilter] = React.useState("");
  const [type, setType] = React.useState<TemplateSelectorType>("MC");
  const [selected, setSelected] = React.useState<ITemplate>(preSelectedTemplate);
  const [importedFormattedExcelDataItems, setImportedFormattedExcelDataItems] = React.useState<string[][]>(null);
  const [showContactForm, setShowContactForm] = React.useState<boolean>(false);
  const [copyIssuesAndTasks, setCopyIssuesAndTasks] = React.useState<boolean>(false);
  const [opprettWasClicked, setOpprettWasClicked] = React.useState<boolean>(false);

  const standardTemplateName = "Standard";
  const tomMalTemplateName = "Tom mal";

  const selectTemplate = (template: ITemplate) => {
    if (onTemplateSelected != null) {
      onTemplateSelected(template);
    }
    setSelected(template);
  };

  const disciplineMap = new Map<string, string>(
    app.name === MCApplicationsUrlEnum.CHECKLIST ? ChecklistDisciplineRoles.map((role) => [role.code, role.title]) : [],
  );
  const dispatch = useAppDispatch();
  const filteredTemplates: ITemplate[] = useMemo(() => {
    let filtered = [];

    if (type === "MC") {
      filtered = templates.filter((template) => template.templateType === "MC");
    }
    if (type === "INSTANCE") {
      filtered = templates.filter((template) => template.templateType === "INSTANCE");
    }
    if (type === "PROJECT") {
      filtered = templates.filter((template) => template.templateType === "PROJECT");
    }

    if (filter !== "") {
      filtered = filtered.filter((template) => template.name.toLowerCase().includes(filter.toLowerCase()));
    }

    return filtered;
  }, [type, templates, filter]);

  let categories: string[] = [];

  if (type === "MC" || type === "PROJECT") {
    categories = [
      ...new Set(filteredTemplates.map((template) => template.category).filter((category) => category != null)),
    ];
  }

  const parentNode: IHierarchyCategory = {
    _id: "main-parent",
    parentId: null,
    props: {
      templateId: "Maler",
      title: null,
    },
  };

  const groupNodes: IHierarchyCategory[] = categories
    .map((category, index) => {
      return {
        _id: `category-placeholder-${index}`,
        parentId: parentNode._id,
        props: {
          templateId: app.name === MCApplicationsUrlEnum.CHECKLIST ? disciplineMap.get(category) : category,
          title: null,
        },
      };
    })
    .sort((a, b) => {
      const aTitle = String(a.props.templateId || "").toLowerCase();
      const bTitle = String(b.props.templateId || "").toLowerCase();
      return aTitle.localeCompare(bTitle);
    });

  let templateNodes: IHierarchyCategory[] = filteredTemplates
    .map((template) => {
      if (template.category === null || template.category === "" || template.templateType === "INSTANCE") {
        return {
          _id: template._id,
          parentId: parentNode._id,
          props: {
            templateId: template.name,
            title: null,
          },
        };
      } else {
        let placeholder: IHierarchyCategory | undefined;

        if (app.name === MCApplicationsUrlEnum.CHECKLIST) {
          const catName = disciplineMap.get(template.category);
          placeholder = groupNodes?.find((ph) => ph.props.templateId === catName);
        } else {
          placeholder = groupNodes?.find((ph) => ph.props.templateId === template.category);
        }

        return {
          _id: template._id,
          parentId: placeholder?._id,
          props: {
            templateId: null,
            title: template.name,
          },
        };
      }
    })
    .sort((a, b) => {
      if (a.props.templateId === standardTemplateName || a.props.templateId === tomMalTemplateName) return -1;

      if (a.props.templateId != null && b.props.templateId != null) {
        return a.props.templateId.localeCompare(b.props.templateId);
      }

      if (a.props.title != null && b.props.title != null) {
        return a.props.title.toLowerCase().localeCompare(b.props.title.toLowerCase());
      }
      return 0;
    });

  const templateTreeNodes = [parentNode, ...groupNodes, ...templateNodes];

  const sortedFinalTemplates = templateTreeNodes.sort((a, b) => {
    const aIsMainParentChild = a.parentId === "main-parent";
    const bIsMainParentChild = b.parentId === "main-parent";
    const aIsParent = templateTreeNodes.some((template) => template.parentId === a._id);
    const bIsParent = templateTreeNodes.some((template) => template.parentId === b._id);

    if (aIsMainParentChild && !aIsParent && (!bIsMainParentChild || bIsParent)) return -1;
    if (bIsMainParentChild && !bIsParent && (!aIsMainParentChild || aIsParent)) return 1;

    if (aIsParent && !bIsParent) return 1;
    if (!aIsParent && bIsParent) return -1;

    return 0;
  });

  const findTemplateById = (templateId: any, templates: any[]): ITemplate | undefined => {
    return templates.find((template) => template._id === templateId);
  };

  const onSubmit = () => {
    setOpprettWasClicked(true);
    if (type === "EXCEL") {
      excelImport?.onCreateFromExcel(importedFormattedExcelDataItems);
    } else {
      onCreate({
        ...selected,
        _copyIssuesAndTasks: copyIssuesAndTasks,
      });
    }
  };

  const onTabChange = (tab: TemplateSelectorType) => {
    if (tab === "MC" && app.name === MCApplicationsUrlEnum.ISSUE) {
      selectTemplate(templates.find((template) => template.name === standardTemplateName));
    }
    if (tab === "MC" && app.name === MCApplicationsUrlEnum.CHECKLIST) {
      selectTemplate(templates.find((template) => template.name === tomMalTemplateName));
    }
    if (tab === "PROJECT") {
      selectTemplate(templates.find((template) => template.templateType === "PROJECT"));
    }
    if (tab === "INSTANCE") {
      selectTemplate(templates.find((template) => template.templateType === "INSTANCE"));
    }
    if (tab === "EXCEL") {
      selectTemplate(null);
    }
    setType(tab);
  };

  const isOpprettButtonDisabled =
    type === "EXCEL"
      ? importedFormattedExcelDataItems?.length === 0 || isTitleEmpty === true || disableOpprettButton === true
      : disableOpprettButton === true || selected == null;

  const send = async (message: string, subject: string) => {
    sendUserMessage(message, subject, () => setShowContactForm(false));
  };

  const sendUserMessage = async (message: string, subject: string, closeModal: () => void) => {
    try {
      await doSendUserMessage(subject, message);
      dispatch(setShowSuccessMessageThunk("Takk for tilbakemeldingen.\nVi vil komme tilbake til deg så snart vi kan."));
      closeModal();
    } catch (error) {
      dispatch(setShowSuccessMessageThunk("Noe gikk galt! Prøv å sende en e-post i stedet."));
    }
  };

  const importValidated = (data: string[][]) => {
    const hasTitleRow =
      Array.isArray(data) && data.length > 0 && data[0].every((cell) => Number.isNaN(Number(String(cell))));
    let dataWithTitleRow: string[][] =
      hasTitleRow === false && app.name === MCApplicationsUrlEnum.CHECKLIST ? [["nr", "navn"], ...data] : data;
    setImportedFormattedExcelDataItems(dataWithTitleRow);
  };

  return (
    <>
      <Modal
        styles={{
          content: { width: "90rem" },
          body: { width: "90rem", height: "40rem", minHeight: "30rem" },
        }}
        size={"auto"}
        opened={true}
        onClose={closeModal}
        title={header}
      >
        <div className="d-flex h-100 p-2 overflow-auto ">
          <div className="d-flex flex-column overflow-hidden" style={{ resize: "horizontal", minWidth: "22rem" }}>
            <SegmentedControl
              color="mc-primary"
              className="w-100 mb-2"
              value={type}
              onChange={(value: TemplateSelectorType) => onTabChange(value)}
              data={[
                ...(showMCButton === true ? [{ label: "MC-maler", value: "MC" as TemplateSelectorType }] : []),
                ...(showProjectButton === true
                  ? [{ label: "Prosjektmaler", value: "PROJECT" as TemplateSelectorType }]
                  : []),
                ...(showInstanceButton === true
                  ? [{ label: "Kopier fra eksisterende", value: "INSTANCE" as TemplateSelectorType }]
                  : []),
                ...(excelImport.enabled === true ? [{ label: "Excel", value: "EXCEL" as TemplateSelectorType }] : []),
              ]}
            />
            {type !== "EXCEL" && (
              <>
                <div className="mb-2">
                  <McInputNew
                    placeholder="Filtrer maler"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    rightSection={<IconSearch size={16} />}
                  />
                  {/* <SearchInput placeholder="Filtrer maler" value={filter} onChange={(e) => setFilter(e.target.value)} /> */}
                </div>
                <div className="d-flex flex-column h-100 overflow-auto" style={{ background: "#FBFBFB" }}>
                  <div className="d-flex flex-column h-100">
                    <div className="d-flex flex-grow-1 overflow-hidden mb-1" style={{ height: "0px" }}>
                      <HierarchyTreeSelector
                        style={{
                          width: "352px",
                          maxWidth: "352px",
                          background: "#ededed",
                        }}
                        isVisible
                        nodeList={sortedFinalTemplates}
                        idProp={"templateId"}
                        titleProp={"title"}
                        selectedNodeIds={[selected?._id]}
                        barLabel="Maler"
                        hideLabelBar={true}
                        onSelectionChange={(node) => {
                          const selectedTemplate = findTemplateById(node[0]?.id, filteredTemplates);
                          selectTemplate(selectedTemplate);
                        }}
                      />
                    </div>
                    <div className="d-flex overflow-hidden">
                      <Button leftSection={<IconMailFilled />} onClick={() => setShowContactForm(true)} color="mc-blue">
                        Forslag til ny mal
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
            {type === "EXCEL" && excelImport?.specificationTable == null && (
              <>
                <Alert color="warning">Mangler spesifikasjonstabell for import</Alert>
              </>
            )}
            {type === "EXCEL" && importedFormattedExcelDataItems == null && excelImport?.specificationTable != null && (
              <ExcelImportValidatorWithSpecTable
                onValidated={(res) => importValidated(res.sourceData as string[][])}
                validators={excelImport?.zodValidation}
                skipTitleRow={true}
                specificationTable={excelImport?.specificationTable}
              />
            )}
            {type === "EXCEL" && importedFormattedExcelDataItems != null && (
              <Alert color="success">{importedFormattedExcelDataItems.length} rader importert fra excel</Alert>
            )}
          </div>
          {type === "MC" ? (
            <Stack bg="lightgray.0" className="w-50 ml-2 p-2" gap="xs">
              {selected != null && descriptionAndAdmin === true && (
                <>
                  <h5 className="d-flex justify-content-center" style={{ textAlign: "center" }}>
                    {selected.name}
                  </h5>
                  <div className="d-flex flex-column flex-grow-1 justify-content-end h-100 overflow-auto">
                    {selected.name !== standardTemplateName && selected.name !== tomMalTemplateName && (
                      <div className="d-flex flex-column align-items-center mb-2">
                        <div style={{ marginRight: "-.5em" }}>
                          <UserAvatar
                            id={selected.admin?._id}
                            name={selected.admin?.name}
                            size="xl"
                          />
                          {/* <Avatar id={selected.admin?._id}  h="50px"/> */}
                        </div>
                        <h5 className="my-1">{selected.admin?.name}</h5>{" "}
                        <div
                          className="memberGroupBadge"
                          style={{ color: `${STANDARD_COLORS.orange}`, border: `3px solid ${STANDARD_COLORS.orange}` }}
                        >
                          ADMIN
                        </div>
                      </div>
                    )}
                    <div className="d-flex h-100 overflow-auto">
                      <p>{selected.description}</p>
                    </div>
                  </div>
                  {bottomDescriptionSection != null && <div className="p-2">{bottomDescriptionSection}</div>}
                </>
              )}
            </Stack>
          ) : null}
          <Stack w="100%" className="ml-2">
            <Flex>{children}</Flex>
            <Stack className=" flex-grow-1 justify-content-between mt-2">
              {isTemplateProject === false && (
                <>
                  {type !== "EXCEL" && (
                    <Stack className="pl-10 pr-10 pt-2">
                      <Flex>
                        <Flex>
                          {(type === "MC" || type === "PROJECT") && <b className="mb-0">Valgt mal</b>}
                          {type === "INSTANCE" && <b className="mb-0">Kopier fra</b>}
                        </Flex>
                        <p className="mb-0">{selected?.name}</p>
                      </Flex>
                      {type === "INSTANCE" && (
                        <Stack>
                          <Flex>
                            <Flex gap="xs">
                              <Title order={6} className="text-nowrap">
                                Kopier innhold
                              </Title>
                              <Tooltip
                                label={
                                  "Velg om kopien skal ta med alt som ligger i registeret, eller kun oppsettet (struktur og innstillinger)."
                                }
                                withArrow
                                zIndex={2042}
                              >
                                <Text c="var(--mantine-color-mc-primary-6)" style={{ cursor: "pointer" }}>
                                  <IconInfoCircle size={20} />
                                </Text>
                              </Tooltip>
                            </Flex>

                            <Flex>
                              <SimpleStampCheck toggled={copyIssuesAndTasks} toggle={setCopyIssuesAndTasks} />
                              <span className="text-nowrap">
                                {copyIssuesAndTasks ? "Ta med innhold" : "Bare innstillinger"}
                              </span>
                            </Flex>
                          </Flex>
                          <Stack>
                            {copyIssuesAndTasks ? (
                              <Stack>
                                <Text size="sm">
                                  <Text fw={700}>Alt innhold blir kopiert</Text>- registeret kopieres med alle saker og
                                  tilhørende aksjoner. Innstillinger og struktur følger også med.
                                </Text>
                                <Flex gap="xs">
                                  <Text size="sm" c="yellow.7" fw={700}>
                                    OBS!
                                  </Text>
                                  <Text size="sm" c="yellow.7">
                                    Ansvarlig for utførelse og eier blir ikke kopiert.
                                  </Text>
                                </Flex>
                              </Stack>
                            ) : (
                              <Stack>
                                <Text size="sm">
                                  <Text fw={700}>Kun innstillinger kopieres</Text>- du får et nytt register med samme
                                  struktur og konfigurasjon, men uten innhold (ingen poster/avvik eller oppgaver).
                                </Text>
                              </Stack>
                            )}
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  )}
                </>
              )}
              <Flex className="w-100 justify-content-center align-items-end flex-grow-1">
                <Button
                  disabled={isOpprettButtonDisabled || createLoading || opprettWasClicked}
                  loading={createLoading || opprettWasClicked}
                  size="md"
                  variant="filled"
                  onClick={() => onSubmit()}
                >
                  {isTemplateProject === false ? "Opprett" : "Opprett ny mal"}
                </Button>
              </Flex>
            </Stack>
          </Stack>
        </div>
      </Modal>
      <Modal size="lg" opened={showContactForm} onClose={() => setShowContactForm(false)} title="Kontakt">
        <ContactForm sendMessage={send} prefilledSubject={"Forslag til ny mal:"} />
      </Modal>
    </>
  );
};

export default TemplateSelector;
